import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area
} from 'recharts'
import {
    ArrowLeft,
    TrendingUp,
    DollarSign,
    Users,
    Briefcase,
    Zap,
    Calendar,
    Download
} from 'lucide-react'
import { organizationsApi, type Organization } from 'lib/api'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Button } from 'components/ui/Button'
import { Badge } from 'components/ui/Badge'
import { formatDate, formatStatsTime } from 'lib/utils'
import { useToast } from 'contexts/ToastContext'
import { PageHeader } from 'components/layout/PageHeader'

interface ConsumptionStats {
    history: Array<{
        date: string
        candidate_creation_count: number
        candidate_creation_invalid_count: number
        candidate_creation_deleted_count: number
        offer_creation_count: number
        offer_creation_deleted_count: number
        offer_modification_count: number
        offer_modification_deleted_count: number
        cost: number
        candidate_input_tokens: number
        candidate_output_tokens: number
        offer_input_tokens: number
        offer_output_tokens: number
    }>
    summary: Organization['consumption']
    trends?: {
        cost: number
        candidate_creation: number
        candidate_creation_invalid: number
        offer_creation: number
        offer_modification: number
        tokens: number
    }
}

type TimeRange = 'day' | 'week' | 'month' | 'year' | 'all';

export function OrganizationStats() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    const { addToast } = useToast()

    const [loading, setLoading] = useState(true)
    const [organization, setOrganization] = useState<Organization | null>(null)
    const [stats, setStats] = useState<ConsumptionStats | null>(null)
    const [timeRange, setTimeRange] = useState<TimeRange>('month')
    const [apiCost, setApiCost] = useState<{
        api_usage: number
        usage_daily: number
        usage_weekly: number
        usage_monthly: number
    } | null>(null)
    const [loadingApiCost, setLoadingApiCost] = useState(false)
    const [lastRefresh, setLastRefresh] = useState<Date | null>(null)

    const fetchData = async (skipLoading = false) => {
        if (!id) return
        try {
            if (!skipLoading) setLoading(true)
            const [orgRes, statsRes] = await Promise.all([
                organizationsApi.getById(parseInt(id)),
                organizationsApi.getConsumptionStats(parseInt(id), timeRange)
            ])
            setOrganization(orgRes.data)
            setStats(statsRes.data)
            setLastRefresh(new Date())
        } catch (error) {
            console.error('Error fetching stats:', error)
            addToast({
                type: 'error',
                title: t('common.error'),
                message: t('organizations.messages.fetchStatsFailed', 'Failed to load statistics'),
                duration: 5000
            })
        } finally {
            if (!skipLoading) setLoading(false)
        }
    }

    // Initial fetch
    useEffect(() => {
        fetchData()
    }, [id, t, addToast, timeRange])

    // Auto-refresh every 20 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            fetchData(true) // Skip loading state for background refresh
        }, 60000 * 20) // 20 minutes

        return () => clearInterval(interval)
    }, [id, timeRange])

    useEffect(() => {
        const fetchApiCost = async () => {
            if (!id) return
            try {
                setLoadingApiCost(true)
                const response = await organizationsApi.getApiCost(parseInt(id))
                setApiCost(response.data)
            } catch (error) {
                console.error('Error fetching API cost:', error)
                setApiCost(null)
            } finally {
                setLoadingApiCost(false)
            }
        }

        fetchApiCost()
    }, [id])

    const chartData = useMemo(() => {
        if (!stats?.history) return []

        return stats.history.map(item => {
            const date = new Date(item.date);
            let formattedDate = formatDate(item.date);

            if (timeRange === 'day') {
                formattedDate = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }

            return {
                ...item,
                formattedDate,
                candidateTotalTokens: item.candidate_input_tokens + item.candidate_output_tokens,
                offerTotalTokens: item.offer_input_tokens + item.offer_output_tokens,
                totalTokens: item.candidate_input_tokens + item.candidate_output_tokens + item.offer_input_tokens + item.offer_output_tokens,
                // Total counts including both existing and deleted
                totalCandidateCreation: item.candidate_creation_count + item.candidate_creation_deleted_count,
                candidateCreationInvalid: item.candidate_creation_invalid_count,
                totalOfferCreation: item.offer_creation_count + item.offer_creation_deleted_count,
                totalOfferModification: item.offer_modification_count + item.offer_modification_deleted_count,
                totalUsageCount: (item.candidate_creation_count + item.candidate_creation_deleted_count + item.candidate_creation_invalid_count) + 
                                (item.offer_creation_count + item.offer_creation_deleted_count) + 
                                (item.offer_modification_count + item.offer_modification_deleted_count)
            };
        });
    }, [stats, timeRange])

    const rangeSummary = useMemo(() => {
        return chartData.reduce((acc, curr) => ({
            total_cost: acc.total_cost + curr.cost,
            candidate_creation_count: acc.candidate_creation_count + curr.candidate_creation_count,
            candidate_creation_invalid_count: acc.candidate_creation_invalid_count + curr.candidateCreationInvalid,
            candidate_creation_deleted_count: acc.candidate_creation_deleted_count + curr.candidate_creation_deleted_count,
            offer_creation_count: acc.offer_creation_count + curr.offer_creation_count,
            offer_creation_deleted_count: acc.offer_creation_deleted_count + curr.offer_creation_deleted_count,
            offer_modification_count: acc.offer_modification_count + curr.offer_modification_count,
            offer_modification_deleted_count: acc.offer_modification_deleted_count + curr.offer_modification_deleted_count,
            input_tokens: acc.input_tokens + curr.candidate_input_tokens + curr.offer_input_tokens,
            output_tokens: acc.output_tokens + curr.candidate_output_tokens + curr.offer_output_tokens,
            total_tokens: acc.total_tokens + curr.totalTokens
        }), { 
            total_cost: 0,
            candidate_creation_count: 0,
            candidate_creation_invalid_count: 0,
            candidate_creation_deleted_count: 0,
            offer_creation_count: 0,
            offer_creation_deleted_count: 0,
            offer_modification_count: 0,
            offer_modification_deleted_count: 0,
            input_tokens: 0, 
            output_tokens: 0,
            total_tokens: 0
        });
    }, [chartData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (!organization || !stats) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-2xl font-bold">{t('organizations.error.notFound', 'Organization not found')}</h2>
                <Button onClick={() => navigate('/organizations')} className="mt-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.back', 'Back')}
                </Button>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <Button variant="outline" size="sm" onClick={() => navigate('/organizations')}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <PageHeader
                      title={organization.name}
                      description={t('organizations.stats.consumptionOverview', 'Consumption Statistics Overview')}
                    />
                </div>
                <div className="flex space-x-2">
                    <div className="flex items-center space-x-2">
                        {lastRefresh && (
                            <span className="text-xs text-muted-foreground">
                                {t('organizations.stats.lastRefresh', { time: formatStatsTime(lastRefresh, i18n.language) })}
                            </span>
                        )}
                    </div>
                    <div className="flex bg-muted rounded-lg p-1">
                        {(['day', 'week', 'month', 'year', 'all'] as TimeRange[]).map((range) => (
                            <Button
                                key={range}
                                variant={timeRange === range ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setTimeRange(range)}
                                className="px-3 h-8 text-xs"
                            >
                                {t(`organizations.stats.timeRanges.${range}`)}
                            </Button>
                        ))}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate('/organizations')}>
                        <Download className="mr-2 h-4 w-4" />
                        {t('common.export', 'Export')}
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('organizations.stats.totalCost', 'Total Cost')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="text-2xl font-bold text-primary">
                                ${rangeSummary.total_cost.toFixed(8)}
                            </div>
                            <DollarSign className="h-8 w-8 text-primary/40" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('organizations.stats.comparisonLabel', {
                                percent: stats?.trends?.cost || 0,
                                range: t(`organizations.stats.timeRanges.${timeRange}`).toLowerCase(),
                                defaultValue: `${stats?.trends?.cost || 0}% from previous ${t(`organizations.stats.timeRanges.${timeRange}`).toLowerCase()}`
                            })}
                        </p>
                        {apiCost && (
                            <div className="mt-2 pt-2 border-t border-primary/10">
                                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                    API Cost ({timeRange === 'day' ? 'Daily' : timeRange === 'week' ? 'Weekly' : 'Monthly'}): ${(
                                        timeRange === 'day' ? apiCost.usage_daily :
                                        timeRange === 'week' ? apiCost.usage_weekly :
                                        apiCost.usage_monthly
                                    ).toFixed(8)}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('organizations.stats.candidateCreation', 'Candidate Creation')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <div className="text-2xl font-bold text-emerald-600">
                                    {rangeSummary.candidate_creation_count + rangeSummary.candidate_creation_deleted_count + rangeSummary.candidate_creation_invalid_count}
                                </div>
                                <div className="flex gap-2 text-xs">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                        ✓ {rangeSummary.candidate_creation_count}
                                    </Badge>
                                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                        ✗ {rangeSummary.candidate_creation_deleted_count}
                                    </Badge>
                                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                        ⚠ {rangeSummary.candidate_creation_invalid_count}
                                    </Badge>
                                </div>
                            </div>
                            <Users className="h-8 w-8 text-emerald-600/40" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('organizations.stats.llmUsageType', 'LLM Usage')}
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('organizations.stats.offerUsage', 'Offer Usage')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <div className="text-2xl font-bold text-blue-600">
                                    {rangeSummary.offer_creation_count + rangeSummary.offer_creation_deleted_count + 
                                     rangeSummary.offer_modification_count + rangeSummary.offer_modification_deleted_count}
                                </div>
                                <div className="space-y-1 text-xs">
                                    <div className="flex gap-1">
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                            ✓ C: {rangeSummary.offer_creation_count}
                                        </Badge>
                                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                            ✗ C: {rangeSummary.offer_creation_deleted_count}
                                        </Badge>
                                    </div>
                                    <div className="flex gap-1">
                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                            ✓ M: {rangeSummary.offer_modification_count}
                                        </Badge>
                                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                            ✗ M: {rangeSummary.offer_modification_deleted_count}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                            <Briefcase className="h-8 w-8 text-blue-600/40" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('organizations.stats.llmUsageType', 'LLM Usage')} (C=Creation, M=Modification)
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('organizations.stats.totalTokens', 'Total Tokens')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <div className="text-xl font-bold text-amber-600">
                                        {rangeSummary.input_tokens.toLocaleString()}
                                    </div>
                                    <div className="text-[10px] font-medium text-amber-600/70">Input</div>
                                </div>
                                <div>
                                    <div className="text-xl font-bold text-amber-600">
                                        {rangeSummary.output_tokens.toLocaleString()}
                                    </div>
                                    <div className="text-[10px] font-medium text-amber-600/70">Output</div>
                                </div>
                            </div>
                            <Zap className="h-8 w-8 text-amber-600/40" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('organizations.stats.aiUsage', 'AI Resource Usage')}
                        </p>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cost Growth Chart */}
                <Card className="col-span-1">
                    <CardHeader>
                        <CardTitle>{t('organizations.stats.costOverTime', 'Cost Accumulation')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="formattedDate" />
                                    <YAxis tickFormatter={(value) => `$${value}`} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload || !payload[0]) return null;
                                            const data = payload[0].payload;
                                            return (
                                                <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                                    <p className="font-semibold text-sm mb-2">{data.formattedDate}</p>
                                                    <div className="space-y-1 text-xs">
                                                        <p className="text-sky-600 font-medium">
                                                            💰 {t('organizations.stats.cost', 'Cost')}: ${data.cost.toFixed(8)}
                                                        </p>
                                                        <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-1">
                                                            <p className="text-emerald-600">
                                                                👤 {t('organizations.stats.candidateCreation', 'Candidate Creation')}: {data.totalCandidateCreation}
                                                            </p>
                                                            <p className="text-blue-600">
                                                                📋 {t('organizations.stats.offerCreation', 'Offer Creation')}: {data.totalOfferCreation}
                                                            </p>
                                                            <p className="text-amber-600">
                                                                ✏️ {t('organizations.stats.offerModification', 'Offer Modification')}: {data.totalOfferModification}
                                                            </p>
                                                            <p className="text-orange-600">
                                                                ⚠ {t('organizations.stats.candidateCreationInvalid', 'Candidate Creation Invalid')}: {data.candidateCreationInvalid}
                                                            </p>
                                                        </div>
                                                        <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-1">
                                                            <p className="text-purple-600">
                                                                ⬇️ Input: {(data.candidate_input_tokens + data.offer_input_tokens).toLocaleString()}
                                                            </p>
                                                            <p className="text-pink-600">
                                                                ⬆️ Output: {(data.candidate_output_tokens + data.offer_output_tokens).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="cost"
                                        stroke="#0ea5e9"
                                        fillOpacity={1}
                                        fill="url(#colorCost)"
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* LLM Usage by Type Chart */}
                <Card className="col-span-1">
                    <CardHeader>
                        <CardTitle>{t('organizations.stats.llmUsageByType', 'LLM Usage by Type')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="formattedDate" />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="totalCandidateCreation"
                                        name={t('organizations.stats.candidateCreation', 'Candidate Creation')}
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="candidateCreationInvalid"
                                        name={t('organizations.stats.candidateCreationInvalid', 'Candidate Creation Invalid')}
                                        stroke="#f97316"
                                        strokeWidth={2}
                                        strokeDasharray="5 3"
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="totalOfferCreation"
                                        name={t('organizations.stats.offerCreation', 'Offer Creation')}
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="totalOfferModification"
                                        name={t('organizations.stats.offerModification', 'Offer Modification')}
                                        stroke="#f59e0b"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Candidate Token Usage Chart */}
                <Card className="col-span-1">
                    <CardHeader>
                        <CardTitle>{t('organizations.stats.candidateTokenUsage', 'Candidate Token Consumption')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="formattedDate" />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Legend />
                                    <Bar dataKey="candidate_input_tokens" name={t('organizations.stats.inputTokens', 'Input Tokens')} fill="#10b981" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="candidate_output_tokens" name={t('organizations.stats.outputTokens', 'Output Tokens')} fill="#059669" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Offer Token Usage Chart */}
                <Card className="col-span-1">
                    <CardHeader>
                        <CardTitle>{t('organizations.stats.offerTokenUsage', 'Offer Token Consumption')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="formattedDate" />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Legend />
                                    <Bar dataKey="offer_input_tokens" name={t('organizations.stats.inputTokens', 'Input Tokens')} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="offer_output_tokens" name={t('organizations.stats.outputTokens', 'Output Tokens')} fill="#2563eb" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
